import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/AppLayout';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Today from './pages/Today';
import Journey from './pages/Journey';
import Community from './pages/Community';
import Progress from './pages/Progress';
import Profile from './pages/Profile';
import Coach from './pages/Coach';
import Seances from './pages/Seances';
import Equipe from './pages/Equipe';
import Rewards from './pages/Rewards';
import Notifications from './pages/Notifications';
import Reglement from './pages/Reglement';
import PhotosAvantApres from './pages/PhotosAvantApres';
import Classement from './pages/Classement';
import Defis from './pages/Defis';
import { Flame } from 'lucide-react';

const Splash = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <img src="/bfc21-logo.png" alt="BFC21" className="w-16 h-16 rounded-full animate-pulse" />
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    loginWithGoogle(sessionId)
      .then(() => {
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/app', { replace: true });
      })
      .catch(() => {
        setError('La connexion Google a échoué. Réessaie.');
        setTimeout(() => navigate('/login', { replace: true }), 1800);
      });
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-center px-6">
      {error ? (
        <p className="text-destructive font-medium">{error}</p>
      ) : (
        <div>
          <img src="/bfc21-logo.png" alt="BFC21" className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Connexion en cours...</p>
        </div>
      )}
    </div>
  );
};

const ProtectedLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Splash />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
};

const CoachRoute = () => {
  const { isAuthenticated, isCoach, loading } = useAuth();
  if (loading) return <Splash />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isCoach) return <Navigate to="/app" replace />;
  return (
    <AppLayout>
      <Coach />
    </AppLayout>
  );
};

const PublicOnly = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Splash />;
  return isAuthenticated ? <Navigate to="/app" replace /> : children;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Auth mode="login" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Auth mode="register" /></PublicOnly>} />
      <Route element={<ProtectedLayout />}>
        <Route path="/app" element={<Today />} />
        <Route path="/parcours" element={<Journey />} />
        <Route path="/seances" element={<Seances />} />
        <Route path="/equipe" element={<Equipe />} />
        <Route path="/recompenses" element={<Rewards />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/reglement" element={<Reglement />} />
        <Route path="/photos" element={<PhotosAvantApres />} />
        <Route path="/classement" element={<Classement />} />
        <Route path="/defis" element={<Defis />} />
        <Route path="/communaute" element={<Community />} />
        <Route path="/progres" element={<Progress />} />
        <Route path="/profil" element={<Profile />} />
      </Route>
      <Route path="/coach" element={<CoachRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" offset={76} richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
